import {
  ErrorType,
  returnValueWithErrorAsync,
} from "cy-util/src/returnValueWithError";
import type { z } from "zod";
import type { ApiResponseError } from "../parseJsonApiResponse";
import { parseJsonApiResponse } from "../parseJsonApiResponse";
import { parseFetchOptions } from "../parseFetchOptions";
import { fetchClient } from "./fetchClient";

/**
 * Creates a fetch function following some conventions based on a configuration
 * You can configure the following arguments
 * - the type of args the method takes
 * - the zod schema to parse the response
 * - the fn to convert convert the args to a url
 * - the fetch options or a fn to create options from the args passed in
 *
 * @returns a fetch function that returns [value, error] turple + a mutationFn prop which can be passed into useMutation tanstack query
 */
export function createApiClientWithArgs<TArgs, TZod extends z.ZodTypeAny>({
  argsFn,
  zod,
}: {
  argsFn: (args: TArgs) => { url: string; options?: RequestInit };
  zod: TZod;
}) {
  const fn = returnValueWithErrorAsync(
    ErrorType<ApiResponseError>,
    async (args: TArgs): Promise<z.infer<TZod>> => {
      const { url, options } = argsFn(args);
      const optionsToSend = parseFetchOptions(options);
      const response = await fetchClient(url, optionsToSend);

      const apiResponse = parseJsonApiResponse(response);
      return apiResponse.then((x) => x.body).then((json) => zod.parse(json));
    },
  );
  const clientFetch = async (args: TArgs): Promise<z.infer<TZod>> => {
    const [val, err] = await fn(args);
    if (err) {
      throw err;
    }
    return val;
  };
  type CombinedFnType = typeof fn & { clientFetch: typeof clientFetch };

  const combinedFn: CombinedFnType = fn as CombinedFnType;
  combinedFn.clientFetch = clientFetch;
  return combinedFn;
}
